import { useCareHistoryDetail, type CareHistoryItem } from "@/lib/api/care";
import { useDevOverridesStore } from "@/lib/hooks/admin/use-dev-overrides";
import { buildRedactedPseudoId } from "@/lib/redacted-display";
import { PostHogEvents, trackEvent } from "@/lib/posthog-events";
import { ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { useState } from "react";
import { CareHistoryDetail } from "./care-history-detail";
import { Pill, formatCareDate, type PillTone } from "./care-primitives";

function tagTone(tag: string): PillTone {
  if (tag.startsWith("Escalation")) return "escalation";
  if (tag.startsWith("Flag")) return "flag";
  if (tag === "Presentation changed") return "changed";
  if (tag === "Presentation review") return "review";
  return "neutral";
}

function splitAssessmentTitle(title: string) {
  const [name, score, severity] = title.split(", ");
  if (!score || !severity || Number.isNaN(Number(score))) return null;
  return { name, score, severity };
}

export function CareHistoryRow({
  menteeId,
  item,
}: {
  menteeId: string;
  item: CareHistoryItem;
}) {
  const [open, setOpen] = useState(false);
  const hideMenteeIdentity =
    useDevOverridesStore((state) => state.hidePhiPii) === true;
  const detail = useCareHistoryDetail(menteeId, item.type, item.id, open);
  const actorName =
    item.actor &&
    hideMenteeIdentity &&
    item.actor.role.toLowerCase() === "mentee"
      ? buildRedactedPseudoId(item.actor.id)
      : item.actor?.name;
  const subtitle =
    item.subtitle ??
    (item.actor
      ? [actorName, item.actor.role].filter(Boolean).join(" • ")
      : null);
  const assessment =
    item.type === "assessment" ? splitAssessmentTitle(item.title) : null;
  const Chevron = open ? ChevronDown : ChevronRight;

  const toggle = () => {
    if (!open) {
      trackEvent(PostHogEvents.CARE_PAGE_HISTORY_ROW_EXPANDED, {
        mentee_id: menteeId,
        row_type: item.type,
      });
    }
    setOpen((value) => !value);
  };

  return (
    <li className="border-b border-[#cbc6be] last:border-b-0">
      <div className="flex min-h-[88px] items-center py-3 pl-2 pr-6">
        <p className="w-[130px] shrink-0 px-4 font-body text-base leading-6 text-black">
          {formatCareDate(item.occurred_at)}
        </p>
        <div className="min-w-0 flex-1 px-4">
          {assessment ? (
            <div className="flex items-center gap-4">
              <p className="font-body text-base leading-6 text-[#1f1e1d]">
                {assessment.name}
              </p>
              <span className="inline-flex h-6 items-center overflow-hidden rounded bg-[rgba(160,153,141,0.16)] font-body text-xs leading-4 text-black">
                <span className="py-0.5 pl-2 pr-1">{assessment.score}</span>
                <span className="h-4 w-px bg-[#cbc6be]" aria-hidden />
                <span className="py-0.5 pl-[5px] pr-2">
                  {assessment.severity}
                </span>
              </span>
            </div>
          ) : (
            <p className="line-clamp-2 font-body text-base leading-6 text-[#1f1e1d]">
              {item.title}
            </p>
          )}
          {subtitle ? (
            <p className="font-body text-xs leading-4 text-[#a0998d]">
              {subtitle}
            </p>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-4">
          <Pill tone={tagTone(item.tag)}>{item.tag}</Pill>
          {item.expandable ? (
            <button
              type="button"
              aria-expanded={open}
              aria-label={open ? "Collapse" : "Expand"}
              onClick={toggle}
              className="inline-flex size-4 items-center justify-center text-[#1f1e1d]"
            >
              <Chevron className="size-4" aria-hidden />
            </button>
          ) : (
            <span className="size-4" />
          )}
        </div>
      </div>
      {open ? (
        <div className="pb-3 pl-2 pr-6">
          <div className="ml-[130px] px-4">
            {detail.isPending ? (
              <div className="flex justify-center py-6">
                <Loader2 className="size-5 animate-spin text-[#a0998d]" />
              </div>
            ) : detail.data ? (
              <CareHistoryDetail detail={detail.data} />
            ) : (
              <p className="font-body text-sm text-states-error">
                Unable to load details.
              </p>
            )}
          </div>
        </div>
      ) : null}
    </li>
  );
}
